'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Product Trading Portal', href: '/product-trading-portal' },
    { label: 'Regulatory Consultancy', href: '/regulatory-consultancy-services' },
    { label: 'About Us', href: '/about-us' },
  ];

  const services = [
    { label: 'Regulatory Compliance', href: '/regulatory-consultancy-services' },
    { label: 'Product Trading', href: '/product-trading-portal' },
    { label: 'Contact Us', href: '/contact' },
  ];

  const certifications = [
    'EU-GMP Certified',
    'WHO-GMP Approved',
    'ANVISA Registered',
    'ISO 9001:2015',
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'BuildingOfficeIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftRightIcon', href: '#' },
    { name: 'Email', icon: 'EnvelopeIcon', href: 'mailto:info@ashabpharma.com' },
  ];

  return (
    <footer className={`bg-trustBuilder text-trustBuilder-foreground ${className}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="relative w-12 h-12">
                <Image
                  src="/assets/images/ASHAB_GROUP_LOGO__3__page-0001-1767693914028.jpg"
                  alt="Ashab Group Logo"
                  width={48}
                  height={48}
                  className="object-contain bg-white rounded-md p-1"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-lg leading-tight">
                  Ashab Group Global
                </span>
              </div>
            </div>
            <p className="text-sm text-trustBuilder-foreground/80 leading-relaxed">
              Your trusted partner in pharmaceutical excellence, connecting Indian manufacturing
              prowess with global regulatory standards across 60+ countries.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="p-2 bg-trustBuilder-foreground/10 hover:bg-primary rounded-md transition-all duration-300 hover:-translate-y-1"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-trustBuilder-foreground/80 hover:text-primary transition-colors duration-300 flex items-center group"
                  >
                    <Icon
                      name="ChevronRightIcon"
                      size={16}
                      className="mr-1 group-hover:translate-x-1 transition-transform duration-300"
                    />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-4">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.href}>
                  <Link
                    href={service.href}
                    className="text-sm text-trustBuilder-foreground/80 hover:text-primary transition-colors duration-300 flex items-center group"
                  >
                    <Icon
                      name="ChevronRightIcon"
                      size={16}
                      className="mr-1 group-hover:translate-x-1 transition-transform duration-300"
                    />
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-4">Certifications</h3>
            <ul className="space-y-3">
              {certifications.map((cert, index) => (
                <li key={index} className="flex items-start">
                  <Icon
                    name="CheckBadgeIcon"
                    size={20}
                    className="mr-2 text-primary flex-shrink-0 mt-0.5"
                    variant="solid"
                  />
                  <span className="text-sm text-trustBuilder-foreground/80">{cert}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-trustBuilder-foreground/20">
              <p className="text-xs text-trustBuilder-foreground/60">
                <Icon name="GlobeAltIcon" size={16} className="inline mr-1" />
                Serving 60+ Countries Worldwide
              </p>
              <p className="text-xs text-trustBuilder-foreground/60 mt-1">
                <Icon name="CubeIcon" size={16} className="inline mr-1" />
                6000+ Quality Products
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-trustBuilder-foreground/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-trustBuilder-foreground/60">
              © {currentYear} Ashab Group. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link
                href="#"
                className="text-sm text-trustBuilder-foreground/60 hover:text-primary transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                href="#"
                className="text-sm text-trustBuilder-foreground/60 hover:text-primary transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <Link
                href="#"
                className="text-sm text-trustBuilder-foreground/60 hover:text-primary transition-colors duration-300"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;